import { useSyncExternalStore } from "react";
import { resolveThemePreference, themeStorageKey } from "@/lib/theme-core.mjs";

// Document-global singleton rather than a provider: the theme lives on
// <html data-theme> and in localStorage, not in the render tree, and
// main.tsx must resolve it before the first React paint (the production CSP
// allows no inline anti-FOUC script).

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const listeners = new Set<() => void>();
let preference: ThemePreference = "system";
let systemDark = false;
let media: MediaQueryList | null = null;

function resolvedTheme(): ResolvedTheme {
  if (preference === "system") return systemDark ? "dark" : "light";
  return preference;
}

function apply() {
  document.documentElement.dataset.theme = resolvedTheme();
}

function notify() {
  for (const listener of listeners) listener();
}

function onSystemChange(event: MediaQueryListEvent) {
  systemDark = event.matches;
  if (preference === "system") {
    apply();
    notify();
  }
}

export function initializeTheme(): void {
  media?.removeEventListener("change", onSystemChange);
  preference = resolveThemePreference(
    window.localStorage.getItem(themeStorageKey),
  );
  media = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null;
  systemDark = media?.matches ?? false;
  media?.addEventListener("change", onSystemChange);
  apply();
  notify();
}

export function setThemePreference(next: ThemePreference): void {
  window.localStorage.setItem(themeStorageKey, next);
  preference = next;
  apply();
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, () => preference);
}

export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, resolvedTheme);
}
